'use client';
import { useEffect, useId, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useMessageContext } from '@ui/components/organisms/message';
import type { ContentType, ContextUserType } from '@ui/constants/context';
import PostItem from './post-item';
import { CommentList, Separator } from '@ui/components';
import { CommentSortTabs, commentSortTabId } from '../comments/comment-sort-tabs';
import { ReplyRefProvider } from '@ui/contexts';
import { PostItemSkeleton } from './post-item-skeleton';
import { MessageUpdateFormPortal } from '../common/message-update-form-portal';
import { MessageUpdateFormProvider } from '@ui/contexts/message-update-form-context';
import { sendGAEvent } from '@next/third-parties/google';
import { PostItemMainRef } from './post-item-main-ref';
import { useAnalytics, useScreenTimeTracking } from '@ui/analytics';

interface Props {
  userType: ContextUserType;
  contentType: ContentType;
  main_post_ref: string;
  isDynamicRoute?: boolean;
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
  moderators_ids: string[];
  onPostViewed?: (resourceId: string) => void | Promise<void>;
  /** Exact route authorization latch; controls background refresh messaging. */
  isPostRouteAuthorized?: boolean;
  /** Any current-route authority request fetching after authorization. */
  isPostRouteRefreshing?: boolean;
}

export const PostView = ({
  userType,
  contentType,
  main_post_ref,
  isDynamicRoute,
  creatorData,
  moderators_ids,
  onPostViewed,
  isPostRouteAuthorized,
  isPostRouteRefreshing,
}: Props) => {
  const messageState = useMessageContext(userType);
  // Legacy callers omit this prop and retain their existing interactive
  // behavior. An explicit false is the feed handoff's read-only first frame.
  const isRouteAuthorized = isPostRouteAuthorized !== false;
  const scrollId = useSearchParams().get('scroll');
  const commentRef = useRef<HTMLDivElement>(null);
  const commentPanelId = useId();
  const { capture } = useAnalytics();
  // Every exact post commits its view events at most once per page session,
  // even when the exact message object is replaced during handoff-to-server
  // reconciliation or the post is revisited after other posts were viewed.
  const committedViewedPostKeysRef = useRef<Set<string>>(new Set());
  // Single-message becomes available asynchronously; we only fire the screen-time
  // hook once we actually have the post so duration reflects real read time.
  const post = messageState.singleMessage;
  const isRefreshingAuthorizedPost = Boolean(
    isRouteAuthorized && post && (messageState.isFetchingSingleMessage || isPostRouteRefreshing),
  );
  const creatorId = post?.community_details?.creator_id ?? post?.creator_id ?? undefined;
  const conversationId =
    post?.community_details?.conversation_id ?? post?.conversation_id ?? undefined;
  // Unambiguous exact post identity for view-event dedupe: post ID plus
  // available creator and conversation IDs. Slug alone is ambiguous because
  // two communities can expose posts under one slug.
  const postViewIdentity = post
    ? JSON.stringify([post.id, creatorId ?? null, conversationId ?? null])
    : null;
  const viewedPostId = post?.id ?? main_post_ref;
  // Access-class failures suppress cached content entirely: no readable post is
  // authorized, so neither screen time nor view analytics may fire.
  const detailAccessError = Boolean(
    messageState.isSingleMessageError &&
      messageState.singleMessageError &&
      typeof messageState.singleMessageError === 'object' &&
      'status' in messageState.singleMessageError &&
      [401, 403, 404, 422].includes(
        Number((messageState.singleMessageError as { status?: number }).status),
      ),
  );

  useScreenTimeTracking({
    enterEvent: 'community_post_viewed',
    exitEvent: 'community_post_exited',
    enabled: Boolean(post) && userType !== 'creator' && !detailAccessError && isRouteAuthorized,
    properties: {
      post_id: viewedPostId,
      creator_id: creatorId,
      user_id: messageState.user_id,
      user_type: userType,
      content_type: contentType,
      is_dynamic_route: isDynamicRoute,
      surface: 'fan',
    },
  });

  useEffect(() => {
    if (
      userType !== 'creator' &&
      isRouteAuthorized &&
      post &&
      !detailAccessError &&
      postViewIdentity &&
      !committedViewedPostKeysRef.current.has(postViewIdentity)
    ) {
      committedViewedPostKeysRef.current.add(postViewIdentity);
      // Keep GA tracking in place for back-compat with existing GA dashboards.
      sendGAEvent('event', 'PostViewPage', {
        evet_label: 'Post View',
        postId: viewedPostId,
      });

      // Keep the legacy `post_viewed` event firing so existing PostHog insights
      // continue to receive data. The richer lifecycle is captured by
      // `community_post_viewed`/`community_post_exited` above.
      capture('post_viewed', {
        post_id: viewedPostId,
        user_type: userType,
        content_type: contentType,
        is_dynamic_route: isDynamicRoute,
        timestamp: new Date().toISOString(),
      });
      void onPostViewed?.(post.id);
    }
  }, [
    capture,
    contentType,
    detailAccessError,
    isDynamicRoute,
    isRouteAuthorized,
    postViewIdentity,
    viewedPostId,
    messageState.singleMessage,
    onPostViewed,
    userType,
  ]);

  useEffect(() => {
    if (isRouteAuthorized && scrollId && messageState.singleMessage) {
      const timer = setTimeout(() => {
        const commentElement = commentRef.current;
        if (commentElement) {
          commentElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
        }
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [isRouteAuthorized, scrollId, messageState.singleMessage]);

  const detailErrorStatus =
    messageState.isSingleMessageError &&
    messageState.singleMessageError &&
    typeof messageState.singleMessageError === 'object' &&
    'status' in messageState.singleMessageError
      ? (messageState.singleMessageError as { status?: number }).status
      : undefined;
  // Access-class failures suppress cached content immediately; other failures
  // (e.g. 5xx) may retain already-authorized readable content with a retry.
  const canRetainReadableContentOnError = Boolean(
    messageState.isSingleMessageError &&
      ![401, 403, 404, 422].includes(Number(detailErrorStatus)) &&
      messageState.singleMessage,
  );

  // Render the exact readable message while the detail query revalidates in
  // the background; skeleton only when no exact readable message exists yet.
  if (messageState.isLoadingSingleMessage && !messageState.singleMessage) {
    return (
      <div className="mx-auto flex w-full max-w-[876px]">
        <PostItemSkeleton />
      </div>
    );
  }

  if (messageState.isSingleMessageError && !canRetainReadableContentOnError) {
    return (
      <div className="m-auto max-w-sm">
        <div className="flex flex-col justify-center space-y-4">
          <div className="text-center">
            <p className="font-nunito text-korDarkGrey text-3xl font-extrabold">
              {messageState?.singleMessageError?.status === 403
                ? 'Oops, post is restricted.'
                : messageState?.singleMessageError?.status === 422
                ? 'Content Removed'
                : 'Something went wrong'}
            </p>
            <p className="font-nunito text-korDarkGrey/40">
              {messageState?.singleMessageError?.status === 422
                ? 'This content is no longer available.'
                : messageState?.singleMessageError?.data?.error || 'Error loading post...'}
            </p>
          </div>
          <Separator className="" />
        </div>
      </div>
    );
  }

  if (!messageState.singleMessage) {
    return <div>No post found</div>;
  }

  const parentPost = messageState.singleMessage.parent_post;
  const rootPost = messageState.singleMessage.main_post;
  // Only a post's own comments re-sort; a comment page's replies keep their order.
  const onSetOrdering = contentType === 'post' ? messageState.onSetOrdering : undefined;

  return (
    <MessageUpdateFormProvider>
      <>
        {isRouteAuthorized && (
          <MessageUpdateFormPortal userType={userType} main_post_ref={main_post_ref} />
        )}

        <div
          data-testid="post-content"
          className="mx-auto flex w-full max-w-[876px] flex-col space-y-4">
          {isRefreshingAuthorizedPost && (
            <div role="status" aria-live="polite" className="text-xs text-gray-500">
              Refreshing post…
            </div>
          )}
          {canRetainReadableContentOnError && (
            <div
              role="alert"
              className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm">
              <span className="font-nunito text-korDarkGrey">
                We couldn&apos;t refresh this post. Showing the last readable version.
              </span>
              <button
                type="button"
                className="font-nunito font-semibold text-primary hover:underline"
                onClick={() => messageState.refetchSingleMessage?.()}>
                Try again
              </button>
            </div>
          )}
          {rootPost && (
            <PostItemMainRef
              mainPostRef={rootPost}
              userType={userType}
              userId={messageState.user_id}
              communityPostRoute={messageState.communityPostRoute}
              communityCommentRoute={messageState.communityCommentRoute}
              communityReplyRoute={messageState.communityReplyRoute}
              query={messageState.query}
              contentType={contentType}
              fullDiscussionRoute={parentPost?.slug}
              isDynamicRoute={isDynamicRoute}
              communityDetails={
                messageState.community_details || messageState.singleMessage.community_details
              }
              moderators_ids={moderators_ids}
              isReadOnly={!isRouteAuthorized}
            />
          )}

          <div ref={commentRef}>
            <PostItem
              key={messageState.singleMessage.id}
              post={messageState.singleMessage}
              userType={userType}
              userId={messageState.user_id}
              creator_id={
                messageState?.singleMessage?.community_details?.creator_id ??
                messageState.singleMessage.creator_id
              }
              communityPostRoute={messageState.communityPostRoute}
              isOwnPage
              isReadOnly={!isRouteAuthorized}
              query={messageState.query}
              contentType={contentType}
              isDynamicRoute={isDynamicRoute}
              communityDetails={
                messageState.community_details || messageState.singleMessage.community_details
              }
              moderators_ids={moderators_ids}
            />
          </div>

          {isRouteAuthorized && contentType !== 'reply' && !messageState.singleMessage.draft && (
            <div className="flex items-center justify-between gap-3">
              <p className="font-nunito text-sm font-semibold">
                {contentType === 'post' ? 'Comments' : 'Replies'}
              </p>
              {onSetOrdering && (
                <CommentSortTabs
                  value={messageState.query.ordering}
                  onValueChange={onSetOrdering}
                  panelId={commentPanelId}
                />
              )}
            </div>
          )}

          {isRouteAuthorized && contentType !== 'reply' && !messageState.singleMessage.draft && (
            <div
              {...(onSetOrdering && {
                id: commentPanelId,
                role: 'tabpanel',
                'aria-labelledby': commentSortTabId(commentPanelId, messageState.query.ordering),
              })}>
              <ReplyRefProvider>
                <CommentList
                  key={`${messageState.singleMessage.child_thread_id || ''}|${
                    messageState.singleMessage.conversation_id || ''
                  }|${
                    messageState.singleMessage.community_details?.creator_id ||
                    messageState.singleMessage.creator_id ||
                    ''
                  }`}
                  contentType={contentType}
                  topLevelComments={contentType === 'post'}
                  userType={userType}
                  post={messageState.singleMessage}
                  creator_id={
                    messageState?.singleMessage?.community_details?.creator_id ??
                    messageState.singleMessage.creator_id
                  }
                  user_id={messageState.user_id}
                  main_post_ref={main_post_ref}
                  enableReply={contentType === 'post'}
                  isDynamicRoute={isDynamicRoute}
                  creatorData={creatorData}
                  moderators_ids={moderators_ids}
                />
              </ReplyRefProvider>
            </div>
          )}
        </div>
      </>
    </MessageUpdateFormProvider>
  );
};
